import Skeleton from "@mui/material/Skeleton";
import React from "react";
import css from "./ContentLoader.module.css";

interface ContentLoaderProps {
  type:
    | "event"
    | "input"
    | "pageHeader"
    | "paragraph"
    | "badge-content"
    | "report-content"
    | "rounded-icon"
    | "short-text"
    | "map";
}

const ReportContentSection: React.FC = () => (
  <div
    style={{
      margin: "0 auto",
      maxWidth: "1200px",
      minWidth: "400px",
    }}
  >
    {/* Summary section */}
    <Skeleton variant="text" sx={{ width: "100%" }} />
    <Skeleton variant="text" sx={{ width: "95%" }} />
    <Skeleton variant="text" sx={{ width: "90%", marginBottom: "1.5rem" }} />

    {/* Section Title */}
    <Skeleton
      variant="text"
      sx={{ fontSize: "1.2rem", width: "40%", marginBottom: "1rem" }}
    />

    {/* Bullet points */}
    <div style={{ marginBottom: "1rem" }}>
      {[...Array(4)].map((_, index) => (
        <div key={index} style={{ display: "flex", marginBottom: "1rem" }}>
          <Skeleton
            variant="circular"
            width={6}
            height={6}
            sx={{ marginRight: "1rem", marginTop: "0.5rem" }}
          />
          <div style={{ flex: 1 }}>
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "100%" }} />
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "95%" }} />
          </div>
        </div>
      ))}
    </div>

    {/* Links section */}
    <div>
      {[...Array(3)].map((_, index) => (
        <Skeleton key={index} variant="text" sx={{ width: "40%" }} />
      ))}
    </div>
  </div>
);

const ContentLoader: React.FC<ContentLoaderProps> = ({ type }) => {
  switch (type) {
    case "event":
      return (
        <>
          <div className={css["content-loader-header"]}>
            <Skeleton
              variant="text"
              sx={{ fontSize: "1.5rem", width: "30%" }}
            />
          </div>
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "90%", marginBottom: "0.5rem" }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "95%", marginBottom: "0.5rem" }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "85%", marginBottom: "1rem" }}
          />

          <Skeleton
            variant="text"
            sx={{
              fontSize: "1.25rem",
              width: "40%",
              marginTop: "16px",
              marginBottom: "0.5rem",
            }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "100%", marginBottom: "1rem" }}
          />

          <Skeleton
            variant="text"
            sx={{
              fontSize: "1.25rem",
              width: "40%",
              marginTop: "16px",
              marginBottom: "0.5rem",
            }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "100%", marginBottom: "1rem" }}
          />

          <Skeleton
            variant="text"
            sx={{
              fontSize: "1.4rem",
              width: "50%",
              marginTop: "16px",
              marginBottom: "0.5rem",
            }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "100%", marginBottom: "1rem" }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "100%", marginBottom: "1rem" }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "100%", marginBottom: "1rem" }}
          />
          <Skeleton
            variant="text"
            sx={{
              fontSize: "1.4rem",
              width: "50%",
              marginTop: "16px",
              marginBottom: "0.5rem",
            }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "100%", marginBottom: "1rem" }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "100%", marginBottom: "1rem" }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "100%", marginBottom: "1rem" }}
          />
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem", width: "100%", marginBottom: "1rem" }}
          />
          <Skeleton variant="text" sx={{ fontSize: "1rem", width: "100%" }} />
        </>
      );

    case "input":
      return (
        <Skeleton
          sx={{ padding: "2px", marginBottom: "20px" }}
          variant="rectangular"
          width={"100%"}
          height={58}
        />
      );
    case "pageHeader":
      return (
        <>
          <Skeleton
            variant="text"
            sx={{ fontSize: "35px", width: "21%", marginBottom: "5px" }}
          />

          <div className={css["content-loader-flex"]}>
            <div className={css["content-loader-badge-item"]}>
              <Skeleton
                variant="rectangular"
                width={140}
                height={24}
                sx={{ borderRadius: "6px" }}
              />
            </div>
            <div className={css["content-loader-badge-item"]}>
              <Skeleton
                variant="rectangular"
                width={140}
                height={24}
                sx={{ borderRadius: "6px" }}
              />
            </div>
          </div>
        </>
      );
    case "paragraph":
      return (
        <div>
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton sx={{ marginBottom: "1rem" }} />
          <Skeleton />
          <Skeleton />
          <Skeleton width={500} />
          <Skeleton width={540} />
          <Skeleton width={510} />
        </div>
      );
    case "badge-content":
      return (
        <div>
          <Skeleton
            variant="rectangular"
            width={100}
            height={"1rem"}
            sx={{ borderRadius: "6px" }}
          />
        </div>
      );

    case "report-content":
      return (
        <>
          <ReportContentSection />
          <ReportContentSection />
        </>
      );
    case "rounded-icon":
      return <Skeleton variant="rounded" width="1.5rem" height="1.5rem" />;
    case "short-text":
      return (
        <Skeleton variant="text" sx={{ width: "100px", marginRight: "1rem" }} />
      );
    case "map":
      return (
        <Skeleton
          variant="rectangular"
          sx={{
            height: "400px",
            width: "100%",
            borderRadius: "8px",
          }}
        />
      );
    default:
      return null;
  }
};

export default ContentLoader;
